import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type SyntheticEvent } from 'react'

import { cn } from '@/lib/utils'
import { sanitizeEmailHtml } from './email-html-sanitize'

interface EmailBodyFrameProps {
  html: string
  className?: string
  title?: string
  /**
   * Grow the frame to the email's height (the default), so the page scrolls
   * as a whole. Off, the caller's className sizes the frame and the email
   * scrolls inside it (the quote send preview's fixed pane).
   */
  autoHeight?: boolean
}

/**
 * The email's rendered height: the body's scroll height. The root element's
 * would never shrink below the frame's current height (in standards mode it
 * is at least the viewport), so a body that got shorter, say after a width
 * change, would keep its old height. The srcDoc's body has no margin and its
 * own padding, so its scroll height is the whole document.
 */
function measureContentHeight(iframe: HTMLIFrameElement): number | null {
  const body = iframe.contentDocument?.body
  if (!body) return null
  const height = Math.ceil(body.scrollHeight)
  return height > 0 ? height : null
}

export function EmailBodyFrame({ html, className, title = 'Email body', autoHeight = true }: EmailBodyFrameProps) {
  const srcDoc = useMemo(() => {
    const sanitized = sanitizeEmailHtml(html)
    return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width"><style>html{color-scheme:light}body{box-sizing:border-box;margin:0;padding:12px;color:#16151a;background:#fff;font:14px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;overflow-wrap:anywhere}img{max-width:100%;height:auto}table{max-width:100%}</style></head><body>${sanitized}</body></html>`
  }, [html])

  const [contentHeight, setContentHeight] = useState<number | null>(null)
  const observerRef = useRef<ResizeObserver | null>(null)

  // The observer belongs to the document it watches, which every srcDoc
  // change replaces: disconnect it then, and on unmount.
  useEffect(
    () => () => {
      observerRef.current?.disconnect()
      observerRef.current = null
    },
    [srcDoc],
  )

  // Runs on each load of the frame's document (React attaches the listener
  // before the srcDoc can load, so the first load is never missed). The
  // ResizeObserver is created in this (the parent) realm and observes the
  // frame's body directly, which `allow-same-origin` permits without any
  // script in the frame: it keeps the height in step as images load and the
  // width changes.
  const handleLoad = useCallback(
    (event: SyntheticEvent<HTMLIFrameElement>) => {
      if (!autoHeight) return
      const iframe = event.currentTarget
      const update = () => {
        const height = measureContentHeight(iframe)
        if (height !== null) setContentHeight(height)
      }
      observerRef.current?.disconnect()
      observerRef.current = null
      update()
      const body = iframe.contentDocument?.body
      if (body && typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(update)
        observer.observe(body)
        observerRef.current = observer
      }
    },
    [autoHeight],
  )

  return (
    <iframe
      className={cn(
        'block w-full border-0 bg-white',
        // A small floor for the moment before the body loads and is measured.
        autoHeight ? 'min-h-12 overflow-hidden' : 'min-h-[160px] overflow-y-auto',
        // Once measured, the frame takes the body's height through a custom property.
        autoHeight && contentHeight !== null ? 'h-(--email-body-height)' : autoHeight && 'h-24',
        className,
      )}
      style={
        autoHeight && contentHeight !== null
          ? ({ '--email-body-height': `${contentHeight}px` } as CSSProperties)
          : undefined
      }
      onLoad={handleLoad}
      referrerPolicy="no-referrer"
      // This frame renders untrusted inbound email HTML, so script execution inside it must be
      // structurally impossible — not merely filtered by the sanitizer (email-html-sanitize.ts).
      // - `allow-scripts` is deliberately omitted. If it were present, a future DOMPurify bypass
      //   that smuggled a <script> or event handler past sanitization could execute. Sanitization
      //   is a filter, not a guarantee; the sandbox is what makes a bypass inert regardless.
      // - `allow-same-origin` is present ONLY so this parent can read the frame's document to
      //   size the frame to its content (contentDocument + a parent-realm ResizeObserver). On its
      //   own it grants nothing to the email: with no script able to run inside the frame there is
      //   nothing to exploit the shared origin. INVARIANT: `allow-same-origin` must NEVER be
      //   paired with `allow-scripts`. Together they would let injected script read/write this
      //   app's own DOM, cookies and localStorage, and even remove its own sandbox. If the frame
      //   ever needs scripts, drop `allow-same-origin` (and auto-height) first.
      //   email-body-frame.test.tsx guards this.
      // - `allow-popups` + `allow-popups-to-escape-sandbox`: without these, every <a> the sanitizer
      //   rewrites to target="_blank" rel="noopener noreferrer" would be inert. Both permit only
      //   user-initiated link navigation and grant no scripting capability.
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={srcDoc}
      title={title}
    />
  )
}
